import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { Package, DownloadSimple, FilePdf, FileDoc, FileXls, PresentationChart, Sparkle } from "@phosphor-icons/react";

const DOC_TYPES = [
  { key: "exec-summary",          label: "Executive Summary",     icon: FilePdf,           formats: ["pdf", "docx"] },
  { key: "one-pager",             label: "One Pager",             icon: FilePdf,           formats: ["pdf"] },
  { key: "pitch-deck",            label: "Pitch Deck",            icon: PresentationChart, formats: ["pdf", "pptx"] },
  { key: "memo",                  label: "Investment Memorandum", icon: FileDoc,           formats: ["pdf", "docx"] },
  { key: "due-diligence",         label: "Due Diligence Report",  icon: FileDoc,           formats: ["pdf", "docx"] },
  { key: "financial-highlights",  label: "Financial Highlights",  icon: FileXls,           formats: ["pdf", "xlsx"] },
  { key: "qa",                    label: "Investor Q&A",          icon: FileDoc,           formats: ["pdf", "docx"], requiresQA: true },
  { key: "data-room",             label: "Data Room Index",       icon: FileXls,           formats: ["pdf", "xlsx"] },
];

export default function ExportCenterTab({ doc, onPackage, busy }) {
  const download = async (kind, fmt) => {
    const t = toast.loading(`Preparing ${fmt.toUpperCase()}…`);
    try {
      const resp = await api.get(`/investment-docs/${doc.id}/export/${kind}.${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data); const a = document.createElement("a"); a.href = url;
      a.download = `${(doc.company_name || "orixa-invest").replace(/\s+/g, "-").toLowerCase()}-${kind}.${fmt}`;
      a.click(); URL.revokeObjectURL(url); toast.success(`${fmt.toUpperCase()} downloaded`, { id: t });
    } catch (err) { toast.error(extractApiError(err, "Download failed"), { id: t }); }
  };

  return (
    <div className="space-y-8" data-testid="idoc-tab-export-body">
      {/* One-click package */}
      <div className="bg-gradient-to-br from-primary to-[hsl(var(--accent))] text-primary-foreground rounded-2xl p-6 sm:p-8 card-elev-lg">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80 mb-2">One-click bundle</div>
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight leading-tight">Generate the complete Investor Package</h2>
            <p className="mt-2 text-sm opacity-90 max-w-xl leading-relaxed">
              PDF + editable DOCX/PPTX/XLSX for every deliverable, plus a README, compressed into a single ZIP archive ready to share.
            </p>
          </div>
          <button
            onClick={onPackage}
            disabled={busy}
            className="bg-white text-primary rounded-lg px-5 py-3 text-sm font-semibold hover:bg-white/90 disabled:opacity-70 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2 shadow-lg"
            data-testid="export-package-btn"
          >
            <Package size={16} weight="bold" /> Generate ZIP
          </button>
        </div>
      </div>

      {/* Individual downloads */}
      <div>
        <div className="text-overline text-muted-foreground mb-4">Individual downloads</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {DOC_TYPES.map((d) => {
            const locked = (d.requiresQA && !(doc.qa || []).length) || (!d.requiresQA && !doc.pack);
            return (
              <div key={d.key} className="bg-card border border-border rounded-2xl p-5 card-elev" data-testid={`export-card-${d.key}`}>
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 rounded-lg bg-teal-soft text-teal grid place-items-center">
                    <d.icon size={18} weight="duotone" />
                  </div>
                  {locked && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">locked</span>}
                </div>
                <div className="mt-4 font-display text-base tracking-tight">{d.label}</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {d.formats.map((fmt) => (
                    <button
                      key={fmt}
                      onClick={() => !locked && download(d.key, fmt)}
                      disabled={locked}
                      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors ${
                        locked ? "border-border text-muted-foreground/50 cursor-not-allowed" : "border-border hover:bg-secondary text-foreground"
                      }`}
                      data-testid={`export-btn-${d.key}-${fmt}`}
                    >
                      <DownloadSimple size={12} weight="bold" /> {fmt.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="text-xs text-muted-foreground text-center max-w-2xl mx-auto leading-relaxed pt-6 border-t border-border">
        Every document is generated in institutional English with the Ibn Miyeta Group design language. Outputs are decision-support materials only — not financial or investment advice.
      </div>
    </div>
  );
}
